import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Info, X, Eye, EyeOff } from 'lucide-react';

/* =============================================================
   Avisos e diálogos do sistema (substituem alert/confirm/prompt)

   avisar(msg)                 → aviso; curto e de sucesso vira "toast",
                                  o resto abre um diálogo para leitura
   confirmar(msg, opcoes)      → Promise<boolean>
   pedirSenha(msg)             → Promise<string | null> (campo mascarado)

   O tipo (sucesso/erro/atenção) é deduzido do texto (✅ ❌ ⚠️ "Erro"),
   para que as mensagens antigas continuem funcionando sem mudança.
============================================================= */

type Tipo = 'sucesso' | 'erro' | 'atencao' | 'info';

interface Toast { id: number; tipo: Tipo; texto: string }
interface Dialogo {
  id: number;
  modo: 'aviso' | 'confirmar' | 'senha';
  tipo: Tipo;
  titulo: string;
  texto: string;
  rotuloConfirmar?: string;
  destrutivo?: boolean;
  resolver: (valor: any) => void;
}

let proximoId = 1;
let toasts: Toast[] = [];
let dialogos: Dialogo[] = [];
const ouvintes = new Set<() => void>();
const notificar = () => ouvintes.forEach(fn => fn());

const EMOJIS_INICIAIS = /^[\s☀-➿️✅✓✔❌⚠ℹ]+|^(\p{Extended_Pictographic}|️|\s)+/u;

const deduzirTipo = (bruto: string): Tipo => {
  const t = bruto.trim();
  if (/^(✅|✓|✔)/.test(t)) return 'sucesso';
  if (/^❌/.test(t) || /^erro\b/i.test(t) || /\bERRO\b/.test(t.slice(0, 40))) return 'erro';
  if (/^(⚠|🔐)/.test(t) || /^(atenção|bloqueado)/i.test(t)) return 'atencao';
  return 'info';
};

const tituloPadrao: Record<Tipo, string> = {
  sucesso: 'Pronto',
  erro: 'Não foi possível concluir',
  atencao: 'Atenção',
  info: 'Aviso',
};

// "ERRO DE VALIDAÇÃO!" → "Erro de validação"
const suavizarCaixaAlta = (s: string) => {
  const letras = s.replace(/[^A-Za-zÀ-ÿ]/g, '');
  if (letras.length > 3 && letras === letras.toUpperCase()) {
    const minus = s.toLowerCase();
    return minus.charAt(0).toUpperCase() + minus.slice(1);
  }
  return s;
};

// Separa título e corpo: primeiro parágrafo curto vira título
const separar = (bruto: string, tipo: Tipo): { titulo: string; texto: string } => {
  const limpo = bruto.replace(EMOJIS_INICIAIS, '').trim();
  const partes = limpo.split(/\n\s*\n/);
  if (partes.length > 1 && partes[0].length <= 70 && !partes[0].includes('\n')) {
    return { titulo: suavizarCaixaAlta(partes[0].replace(/[!:]+$/, '').trim()), texto: partes.slice(1).join('\n\n').trim() };
  }
  return { titulo: tituloPadrao[tipo], texto: limpo };
};

export const avisar = (mensagem: string) => {
  const tipo = deduzirTipo(mensagem);
  const limpo = mensagem.replace(EMOJIS_INICIAIS, '').trim();
  if (tipo === 'sucesso' && limpo.length <= 140 && !limpo.includes('\n')) {
    const id = proximoId++;
    toasts = [...toasts, { id, tipo, texto: limpo }];
    notificar();
    setTimeout(() => { toasts = toasts.filter(t => t.id !== id); notificar(); }, 5000);
    return;
  }
  const { titulo, texto } = separar(mensagem, tipo);
  dialogos = [...dialogos, { id: proximoId++, modo: 'aviso', tipo, titulo, texto, resolver: () => {} }];
  notificar();
};

export const confirmar = (mensagem: string, opcoes: { titulo?: string; rotuloConfirmar?: string; destrutivo?: boolean } = {}): Promise<boolean> =>
  new Promise(resolve => {
    const destrutivo = opcoes.destrutivo ?? /(excluir|deletar|remover|cancelar|não pode ser desfeita)/i.test(mensagem);
    const { titulo, texto } = separar(mensagem, destrutivo ? 'atencao' : 'info');
    dialogos = [...dialogos, {
      id: proximoId++, modo: 'confirmar', tipo: destrutivo ? 'atencao' : 'info',
      titulo: opcoes.titulo || (titulo === tituloPadrao.atencao || titulo === tituloPadrao.info ? 'Confirmar ação' : titulo),
      texto, rotuloConfirmar: opcoes.rotuloConfirmar || 'Confirmar', destrutivo, resolver: resolve,
    }];
    notificar();
  });

export const pedirSenha = (mensagem: string, opcoes: { titulo?: string; rotuloConfirmar?: string } = {}): Promise<string | null> =>
  new Promise(resolve => {
    dialogos = [...dialogos, {
      id: proximoId++, modo: 'senha', tipo: 'atencao',
      titulo: opcoes.titulo || 'Confirme com sua senha',
      texto: mensagem.replace(EMOJIS_INICIAIS, '').trim(),
      rotuloConfirmar: opcoes.rotuloConfirmar || 'Confirmar', destrutivo: true, resolver: resolve,
    }];
    notificar();
  });

const estilos: Record<Tipo, { Icone: React.FC<any>; cor: string; fundo: string }> = {
  sucesso: { Icone: CheckCircle2, cor: 'text-emerald-700', fundo: 'bg-emerald-100' },
  erro: { Icone: XCircle, cor: 'text-red-700', fundo: 'bg-red-100' },
  atencao: { Icone: AlertTriangle, cor: 'text-amber-700', fundo: 'bg-amber-100' },
  info: { Icone: Info, cor: 'text-[#0B3B8C]', fundo: 'bg-blue-100' },
};

const DialogoView: React.FC<{ d: Dialogo; fechar: (valor: any) => void }> = ({ d, fechar }) => {
  const [senha, setSenha] = useState('');
  const [mostrar, setMostrar] = useState(false);
  const botaoRef = useRef<HTMLButtonElement>(null);
  const senhaRef = useRef<HTMLInputElement>(null);
  const { Icone, cor, fundo } = estilos[d.tipo];
  const cancelar = () => fechar(d.modo === 'confirmar' ? false : d.modo === 'senha' ? null : undefined);
  const ok = () => fechar(d.modo === 'confirmar' ? true : d.modo === 'senha' ? (senha || null) : undefined);

  useEffect(() => {
    const anterior = document.activeElement as HTMLElement | null;
    (d.modo === 'senha' ? senhaRef.current : botaoRef.current)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancelar(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('keydown', onKey, true); anterior?.focus?.(); };
  }, []);

  return (
    <div className="fixed inset-0 z-[200] bg-slate-950/55 backdrop-blur-[2px] flex items-center justify-center p-4 modal-backdrop">
      <form
        role="alertdialog" aria-modal="true" aria-labelledby={`dlg-t-${d.id}`} aria-describedby={`dlg-d-${d.id}`}
        onSubmit={(e) => { e.preventDefault(); ok(); }}
        className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden modal-content"
      >
        <div className="p-6 flex gap-4">
          <div className={`w-11 h-11 rounded-full ${fundo} flex items-center justify-center flex-shrink-0`} aria-hidden="true">
            <Icone className={`w-6 h-6 ${cor}`} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id={`dlg-t-${d.id}`} className="text-lg font-bold text-slate-900 leading-snug">{d.titulo}</h2>
            {d.texto && (
              <p id={`dlg-d-${d.id}`} className="mt-2 text-[15px] text-slate-700 leading-relaxed whitespace-pre-line break-words max-h-[50vh] overflow-y-auto">{d.texto}</p>
            )}
            {d.modo === 'senha' && (
              <div className="relative mt-4">
                <label htmlFor={`dlg-s-${d.id}`} className="sr-only">Senha</label>
                <input
                  id={`dlg-s-${d.id}`} ref={senhaRef} type={mostrar ? 'text' : 'password'} autoComplete="current-password"
                  value={senha} onChange={(e) => setSenha(e.target.value)} placeholder="Sua senha"
                  className="w-full h-12 pl-4 pr-12 rounded-xl border border-slate-300 focus:border-[#0B57D0] focus:ring-2 focus:ring-[#0B57D0]/25 focus:outline-none text-[15px]"
                />
                <button type="button" onClick={() => setMostrar(v => !v)} aria-label={mostrar ? 'Ocultar senha' : 'Mostrar senha'}
                  className="absolute right-1 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100">
                  {mostrar ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          {d.modo !== 'aviso' && (
            <button type="button" onClick={cancelar}
              className="h-11 px-5 rounded-xl border border-slate-300 bg-white text-sm font-semibold text-slate-800 hover:bg-slate-100">
              Cancelar
            </button>
          )}
          <button ref={botaoRef} type="submit" disabled={d.modo === 'senha' && !senha}
            className={`h-11 px-6 rounded-xl text-sm font-bold text-white ${d.destrutivo ? 'bg-red-700 hover:bg-red-800' : 'bg-[#08162D] hover:bg-[#13284D]'}`}>
            {d.modo === 'aviso' ? 'Entendi' : d.rotuloConfirmar}
          </button>
        </div>
      </form>
    </div>
  );
};

// Fecha um modal com a tecla Esc (ignorado enquanto um diálogo do sistema está aberto)
export const EscParaFechar: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const ref = useRef(onClose);
  ref.current = onClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && dialogos.length === 0) ref.current(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  return null;
};

export const FeedbackHost: React.FC = () => {
  const [, forcar] = useState(0);
  useEffect(() => {
    const fn = () => forcar(n => n + 1);
    ouvintes.add(fn);
    return () => { ouvintes.delete(fn); };
  }, []);

  const atual = dialogos[0];
  const fechar = (valor: any) => {
    if (!atual) return;
    dialogos = dialogos.slice(1);
    atual.resolver(valor);
    notificar();
  };

  return (
    <>
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 sm:left-auto sm:right-6 sm:translate-x-0 z-[210] flex flex-col gap-2 w-[calc(100%-2rem)] sm:w-96 pointer-events-none"
        role="status" aria-live="polite">
        {toasts.map(t => {
          const { Icone, cor } = estilos[t.tipo];
          return (
            <div key={t.id} className="pointer-events-auto flex items-start gap-3 bg-white border border-slate-200 shadow-lg rounded-xl px-4 py-3 modal-content">
              <Icone className={`w-5 h-5 flex-shrink-0 mt-0.5 ${cor}`} aria-hidden="true" />
              <p className="flex-1 text-sm text-slate-800 leading-snug">{t.texto}</p>
              <button onClick={() => { toasts = toasts.filter(x => x.id !== t.id); notificar(); }}
                className="w-7 h-7 -mr-1 flex items-center justify-center rounded-md text-slate-400 hover:text-slate-800 hover:bg-slate-100" aria-label="Fechar aviso">
                <X className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
      {atual && <DialogoView key={atual.id} d={atual} fechar={fechar} />}
    </>
  );
};
